import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '@/context/CartContext';

const navLinks = [
  { to: '/shop', label: 'Shop', icon: 'ri-store-2-line' },
  { to: '/gifting', label: 'Gifting', icon: 'ri-gift-line' },
  { to: '/about', label: 'About', icon: 'ri-information-line' },
  { to: '/contact', label: 'Contact', icon: 'ri-customer-service-2-line' },
];

export default function Header() {
  const { itemCount, openCart } = useCart();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const isActive = (to: string) => {
    if (to === '/shop') return location.pathname === '/shop';
    return location.pathname === to;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        menuOpen || scrolled ? 'bg-background-50/95 backdrop-blur-md shadow-md' : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <span className="font-heading text-xl md:text-2xl font-bold text-primary-500 tracking-tight">
              Pounce<span className="relative inline-flex items-center justify-center w-5 h-5 ml-0.5">
                <i className="ri-footprint-fill text-accent-500 text-lg paw-icon transition-transform duration-300 group-hover:scale-125"></i>
              </span>Bounce
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  isActive(link.to)
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right icons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-primary-50 text-foreground-600 hover:text-primary-500 transition-colors duration-200"
              aria-label="Search"
            >
              <i className="ri-search-line text-lg"></i>
            </button>

            <button
              onClick={openCart}
              className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-primary-50 text-foreground-600 hover:text-primary-500 transition-colors duration-200"
              aria-label={`Cart with ${itemCount} items`}
            >
              <i className="ri-shopping-bag-line text-lg"></i>
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 flex items-center justify-center bg-accent-500 text-background-50 text-xs font-bold rounded-full animate-pulse">
                  {itemCount > 9 ? '9+' : itemCount}
                </span>
              )}
            </button>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-primary-50 text-foreground-600 transition-colors duration-200"
              aria-label="Toggle menu"
            >
              <i className={`text-lg ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-background-200/70 bg-background-50 rounded-b-2xl shadow-lg mb-2">
            <div className="flex flex-col gap-1 p-3">
              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActive(link.to)
                      ? 'text-primary-500 bg-primary-50'
                      : 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50'
                  }`}
                >
                  <i className={`${link.icon} text-lg`}></i>
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-background-200/70 mt-1 pt-2">
                <Link
                  to="/shop?pet=dog"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors duration-200"
                >
                  <i className="ri-footprint-line text-lg"></i>
                  For Dogs
                </Link>
                <Link
                  to="/shop?pet=cat"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors duration-200"
                >
                  <i className="ri-emotion-line text-lg"></i>
                  For Cats
                </Link>
                <Link
                  to="/shop?function=everyday-accessories"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors duration-200"
                >
                  <i className="ri-shirt-line text-lg"></i>
                  Accessories
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Search bar dropdown */}
        {searchOpen && (
          <div className="md:hidden border-t border-background-200/70 bg-background-50 rounded-b-2xl shadow-lg mb-2">
            <div className="p-4">
              <input
                type="text"
                placeholder="Search products..."
                className="w-full px-4 py-3 bg-secondary-100 rounded-xl text-sm text-foreground-700 placeholder:text-foreground-400 border-none outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}